import { useState, useEffect } from 'react';
import throttle from 'lodash.throttle';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel } from 'swiper/modules';

import Heading from '@components/ui/Heading';

import PromoBox from '@components/cms/Slices/PrimaryPromoBoxes/PromoBox';
import {
  SecondaryPromoBoxesSliceData,
  PromoBoxItem,
  PageType,
} from '@adapters/prismic/types/slices';
import { eePromoClickHandler } from '@utils/gtm';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';

import styles from './SecondaryPromoBoxes.module.scss';

export interface SecondaryPromoBoxesProps {
  title: SecondaryPromoBoxesSliceData['primary']['title'];
  items: SecondaryPromoBoxesSliceData['items'];
  trackPromos: boolean;
  pageType: PageType;
  sliceIndex: number;
}

const SecondaryPromoBoxes = ({
  title,
  items,
  trackPromos,
  pageType,
  sliceIndex,
}: SecondaryPromoBoxesProps) => {
  const [slidesOffsetBefore, setSlidesOffsetBefore] = useState<null | number>(
    null
  );

  const XL_BREAKPOINT = 1260;
  const XXL_BREAKPOINT = 1616;

  const calculateSlidesOffsetBefore = (width: number) => {
    if (width < XL_BREAKPOINT) return 0;
    if (width >= XL_BREAKPOINT && width < XXL_BREAKPOINT)
      return (width - 1264) / 2;
    return (width - 1360) / 2;
  };

  useEffect(() => {
    const updateWidthAndOffset = () => {
      const currentWidth = window.innerWidth;

      setSlidesOffsetBefore(calculateSlidesOffsetBefore(currentWidth));
    };

    updateWidthAndOffset();
    const handleResizeWindow = throttle(updateWidthAndOffset, 100);

    window.addEventListener('resize', handleResizeWindow);
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  if (!items) return null;

  const slides = items?.map((item: PromoBoxItem, index: number) => {
    const promoId = item?.heading_red_text
      ? `${item?.heading_black_text} ${item?.heading_red_text}`
      : item?.heading_black_text;

    const promoDetails = {
      id: promoId,
      name: `${promoId}-${pageType}-PromoboxCarousel`,
      type: 'image',
      url: item?.url,
      position: `${pageType}-${sliceIndex}-PromoboxCarousel${index}`,
      'co-op_brand_attribute': item?.['co-op_brand_attribute'] || null,
    };

    return (
      <SwiperSlide
        key={`secondary-promo-box-${item?.url}`}
        className={styles.swiperSlide}
      >
        <a
          href={item?.url}
          className={styles.promoBoxLink}
          onClick={() => eePromoClickHandler(promoDetails)}
        >
          <PromoBox
            item={item}
            isSecondaryPromoBox
            trackPromos={trackPromos}
            pageType={pageType}
            sliceIndex={sliceIndex}
            positionWithinSlice={index}
          />
        </a>
      </SwiperSlide>
    );
  });

  return (
    <div className={styles.container}>
      {title && (
        <div className={styles.titleContainer}>
          <Heading semanticLevel="h2" styleLevel="2">
            {title}
          </Heading>
        </div>
      )}
      {slidesOffsetBefore !== null && (
        <Swiper
          modules={[FreeMode, Mousewheel]}
          className={styles.swiper}
          slidesPerView={'auto'}
          initialSlide={0}
          spaceBetween={12}
          slidesOffsetBefore={slidesOffsetBefore}
          mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
          freeMode={{ enabled: true }}
        >
          {slides}
        </Swiper>
      )}
    </div>
  );
};

export default SecondaryPromoBoxes;
